import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/configure-store'
import { newHttpService } from '../../services/new-http-service'
import { SelectCompanyPrompt } from '../../cmps/general/select-company-prompt'
import { EntitySystemTable } from '../../cmps/entity-system/entity-system-table'
import { EntitySystemFormModal } from '../../cmps/entity-system/entity-system-form-modal'
import { getEntitySystemsByCompanyNameQuery } from '../../cmps/util-cmps/graphql-queries/entity-systems-query'
import { Spinner } from '../../cmps/util-cmps/spinner'
import { useCachedFetch } from '../../hooks/use-cached-fetch'

export const EntitySystemsPage: React.FC = () => {
    const companyName = useSelector((stateModule: RootState) => stateModule.companyName.value)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [entitySystemToEdit, setEntitySystemToEdit] = useState<any>(null)

    const { data, isLoading, hasError, refetch } = useCachedFetch(
        companyName ? `entity-systems:${companyName}` : null,
        async () => {
            const query = getEntitySystemsByCompanyNameQuery(companyName)
            const result = await newHttpService.graphql(companyName, query)
            return (result.entitySystemsByCompanyName || []) as any[]
        }
    )

    const openCreateModal = () => {
        setEntitySystemToEdit(null)
        setIsModalOpen(true)
    }

    const openEditModal = (entitySystem: any) => {
        setEntitySystemToEdit(entitySystem)
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setEntitySystemToEdit(null)
    }

    const handleSuccess = () => {
        closeModal()
        refetch()
    }

    if (!companyName) {
        return <div className="tablePageContainer"><SelectCompanyPrompt /></div>
    }

    return <div className="tablePageContainer">
        <div className="page-header">
            <p className="tableSubtitle">Manage entity systems for the selected company.</p>
            <button className="add-btn" onClick={openCreateModal}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Create Entity System
            </button>
        </div>

        {isLoading && <Spinner />}
        {!isLoading && !hasError && <EntitySystemTable data={data || []} onEditClick={openEditModal} />}
        {!isLoading && hasError && <div className="error-hint">Failed to load data. Try refreshing the page or re-logging in.</div>}

        {isModalOpen && (
            <EntitySystemFormModal
                key={entitySystemToEdit?.id || 'create'}
                companyName={companyName}
                entitySystemToEdit={entitySystemToEdit}
                existingEntitySystems={data || []}
                onSuccess={handleSuccess}
                onClose={closeModal}
            />
        )}
    </div>
}
